<script lang="ts">
  import InspectorField from '../InspectorField.svelte';
  import ConfirmDialog from '../../ui/ConfirmDialog.svelte';
  import { getErrorMessage } from '$lib/api';
  import { computeUnitWorkflow } from '$lib/workflow';
  import { clearEntity } from '$lib/router';
  import { inputDevicesQuery } from '$lib/queries/input_devices';
  import { roomsQuery } from '$lib/queries/rooms';
  import { zonesQuery } from '$lib/queries/zones';
  import { loadsQuery } from '$lib/queries/loads';
  import { rcusQuery } from '$lib/queries/rcus';
  import { dinModulesForUnitQuery } from '$lib/queries/din_modules';
  import { rcuChannelsQuery } from '$lib/queries/rcu_channels';
  import { scenesQuery } from '$lib/queries/scenes';
  import { unitSceneLoadsQuery } from '$lib/queries/scene_loads';
  import { productsQuery } from '$lib/queries/products';
  import { unitsQuery } from '$lib/queries/units';
  import {
    updateInputDeviceMutation,
    deleteInputDeviceMutation
  } from '$lib/mutations/input_devices';
  import { updateInputKeyMutation } from '$lib/mutations/input_keys';
  import { invalidateAfterInputDeviceUpdate } from '$lib/state/invalidation';
  import { keypadCanvasUI } from '$lib/state/ui/keypadCanvas.svelte';
  import { router } from '$lib/router';
  import type { InputDeviceResponse } from '$lib/types/input_device';

  let { id }: { id: number } = $props();

  const DEVICE_TYPE_OPTIONS = ['standard_keypad', 'corridor_keypad', 'keycard', 'logic_board'];
  const CAT6_BRANCHES = [1, 3, 4, 5, 6, 7, 8];

  const units = unitsQuery();
  const zones = zonesQuery();
  const rooms = roomsQuery();
  const loads = loadsQuery();
  const devices = inputDevicesQuery();
  const rcus = rcusQuery();
  const dinModules = dinModulesForUnitQuery();
  const rcuChannels = rcuChannelsQuery();
  const scenes = scenesQuery();
  const sceneLoads = unitSceneLoadsQuery();
  const products = productsQuery();

  const updateDevice = updateInputDeviceMutation();
  const deleteDevice = deleteInputDeviceMutation();
  const updateKeyName = updateInputKeyMutation();

  let unitId = $derived(router.unitId ?? 0);
  let unit = $derived((units.data ?? []).find((u) => u.id === unitId) ?? null);
  let unitDevices = $derived(devices.data ?? []);
  let device = $derived(unitDevices.find((d) => d.id === id));

  let sortedKeys = $derived(
    [...(device?.keys ?? [])].sort((a, b) => (a.key_index ?? 0) - (b.key_index ?? 0))
  );

  let selectedKey = $derived(
    sortedKeys.find((k) => k.id === keypadCanvasUI.selectedKeyId) ?? sortedKeys[0] ?? null
  );

  $effect(() => {
    if (sortedKeys.length && !sortedKeys.some((k) => k.id === keypadCanvasUI.selectedKeyId)) {
      keypadCanvasUI.selectedKeyId = sortedKeys[0]?.id ?? null;
    }
  });

  let inputKeys = $derived((devices.data ?? []).flatMap((d) => d.keys ?? []));

  let channels = $derived(
    (rcuChannels.data ?? []).map((c) => ({ load: c.load, object_id: c.rcu }))
  );

  let workflow = $derived(
    computeUnitWorkflow({
      unit: unit,
      zones: zones.data ?? [],
      rooms: rooms.data ?? [],
      loads: loads.data ?? [],
      inputDevices: unitDevices,
      inputKeys,
      rcus: rcus.data ?? [],
      dinModules: dinModules.data ?? [],
      channels,
      scenes: scenes.data ?? [],
      sceneLoads: sceneLoads.data ?? [],
      products: products.data ?? []
    })
  );

  let duplicateAddress = $derived(
    device &&
      device.keys[0]?.address != null &&
      (unitDevices.filter(
        (d) => d.keys[0]?.address != null && d.keys[0].address === device!.keys[0].address
      ).length > 1 ||
        workflow.entities.duplicateAddresses.some(
          (g: { address: number }) => g.address === device!.keys[0].address
        ))
  );

  let showDeleteConfirm = $state(false);
  let selectError = $state('');

  async function saveRoom(event: Event) {
    const location_room = Number((event.currentTarget as HTMLSelectElement).value);
    if (!location_room || location_room === device?.location_room) return;
    selectError = '';
    try {
      await updateDevice.mutateAsync({ id, data: { location_room } });
      invalidateAfterInputDeviceUpdate(unitId);
    } catch (e) {
      selectError = getErrorMessage(e) || 'Failed to move keypad';
    }
  }

  async function saveCat6Branch(event: Event) {
    const cat6_branch = Number((event.currentTarget as HTMLSelectElement).value);
    if (!cat6_branch || cat6_branch === device?.cat6_branch) return;
    selectError = '';
    try {
      await updateDevice.mutateAsync({ id, data: { cat6_branch } });
      invalidateAfterInputDeviceUpdate(unitId);
    } catch (e) {
      selectError = getErrorMessage(e) || 'Failed to update CAT-6 branch';
    }
  }

  async function saveType(event: Event) {
    const type = (event.currentTarget as HTMLSelectElement).value;
    if (type === device?.type) return;
    selectError = '';
    try {
      await updateDevice.mutateAsync({ id, data: { type: type as InputDeviceResponse['type'] } });
      invalidateAfterInputDeviceUpdate(unitId);
    } catch (e) {
      selectError = getErrorMessage(e) || 'Failed to update device type';
    }
  }

  async function saveKeyName(name: string) {
    if (!selectedKey) return;
    const trimmed = name.trim();
    if (trimmed === (selectedKey.name ?? '')) return;
    await updateKeyName.mutateAsync({ id: selectedKey.id, data: { name: trimmed } });
  }

  async function saveKeyAddress(addressStr: string) {
    if (!selectedKey) return;
    const address = parseInt(addressStr, 10);
    if (isNaN(address)) return;
    if (address === selectedKey.address) return;
    await updateKeyName.mutateAsync({ id: selectedKey.id, data: { address } });
  }

  async function saveKeyGroup(groupStr: string) {
    if (!selectedKey) return;
    const group = parseInt(groupStr, 10);
    if (isNaN(group)) return;
    if (group === selectedKey.group_id) return;
    await updateKeyName.mutateAsync({ id: selectedKey.id, data: { group_id: group } });
  }

  async function handleDelete() {
    showDeleteConfirm = false;
    if (!device) return;
    await deleteDevice.mutateAsync(id);
    invalidateAfterInputDeviceUpdate(unitId);
    clearEntity();
  }
</script>

{#if device}
  <div class="px-3 py-3 space-y-4">
    <div class="space-y-2">
      <div class="space-y-1">
        <label class="block text-2xs font-medium text-text-muted uppercase tracking-wide">
          Device type
        </label>
        <select
          class="w-full px-2 py-1.5 text-sm border border-border-strong rounded bg-bg-canvas text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
          value={device.type}
          onchange={saveType}
        >
          {#each DEVICE_TYPE_OPTIONS as opt}
            <option value={opt}>{opt}</option>
          {/each}
        </select>
      </div>

      {#if duplicateAddress}
        <div
          class="px-2 py-1 text-2xs rounded border border-state-warn/30 bg-state-warn/5 text-state-warn"
        >
          Duplicate address — another device uses address {device.keys[0]?.address}
        </div>
      {/if}

      <div class="space-y-1">
        <label class="block text-2xs font-medium text-text-muted uppercase tracking-wide">
          Room
        </label>
        <select
          class="w-full px-2 py-1.5 text-sm border border-border-strong rounded bg-bg-canvas text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
          value={device.location_room ?? ''}
          onchange={saveRoom}
        >
          <option value="" disabled>Select room</option>
          {#each rooms.data ?? [] as room}
            <option value={room.id}>{room.name}</option>
          {/each}
        </select>
      </div>

      <div class="space-y-1">
        <label class="block text-2xs font-medium text-text-muted uppercase tracking-wide">
          Number of keys
        </label>
        <p class="text-sm text-text-primary font-mono">{device.keys.length}</p>
      </div>

      {#if selectedKey}
        <InspectorField
          label="Button {(selectedKey.key_index ?? 0) + 1} — name"
          value={selectedKey.name ?? ''}
          placeholder={`Key ${(selectedKey.key_index ?? 0) + 1}`}
          onSave={saveKeyName}
        />
        <InspectorField
          label="Button {(selectedKey.key_index ?? 0) + 1} — address"
          type="text"
          value={String(selectedKey.address)}
          mono
          onSave={saveKeyAddress}
        />
        <InspectorField
          label="Button {(selectedKey.key_index ?? 0) + 1} — group"
          type="text"
          value={String(selectedKey.group_id)}
          mono
          onSave={saveKeyGroup}
        />
      {/if}

      <div class="space-y-1">
        <label class="block text-2xs font-medium text-text-muted uppercase tracking-wide">
          CAT-6 branch
        </label>
        <select
          class="w-full px-2 py-1.5 text-sm border border-border-strong rounded bg-bg-canvas text-text-primary focus:outline-none focus:ring-1 focus:ring-accent-primary"
          value={device.cat6_branch}
          onchange={saveCat6Branch}
        >
          {#each CAT6_BRANCHES as branch}
            <option value={branch}>{branch}</option>
          {/each}
        </select>
      </div>

      {#if selectError}
        <p class="text-2xs text-state-error">{selectError}</p>
      {/if}
    </div>

    <div class="pt-4 border-t border-border-default space-y-2">
      <p class="text-2xs font-medium text-text-muted uppercase tracking-wide">Danger zone</p>
      <button
        type="button"
        class="w-full px-2 py-1.5 text-sm text-state-error border border-state-error/30 rounded hover:bg-state-error/5 transition-colors duration-100"
        onclick={() => (showDeleteConfirm = true)}
      >
        Delete device
      </button>
    </div>
  </div>
{:else}
  <div class="px-3 py-3 text-sm text-text-muted">Device not found</div>
{/if}

<ConfirmDialog
  bind:open={showDeleteConfirm}
  title="Delete device"
  message="Delete device at address {device?.keys[0]
    ?.address}? All key programming will be removed."
  confirmText="Delete"
  onconfirm={handleDelete}
  oncancel={() => (showDeleteConfirm = false)}
/>
